/**
 * Transparent, speaker-compatible rhythm cues.
 *
 * This lane is intentionally semantic-free: it produces short audible ticks,
 * never spoken words or hidden payloads. The main voice remains responsible
 * for the opening body-scan/anchor text. The cue controller only owns timing,
 * level, and pause-safe transport behavior.
 */

export const AUDIBLE_RHYTHM_DEFAULTS = Object.freeze({
  enabled: false,
  bpm: 42,
  level: 0.035,
  toneHz: 560,
  accentToneHz: 720,
  tickDurationMs: 82,
  accentDurationMs: 128,
  accentEvery: 4,
  openingSeconds: 90,
  reanchorIntervalSeconds: 1500,
  reanchorSeconds: 18,
});

const clamp = (value, minimum, maximum, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, number)) : fallback;
};

export function normalizeAudibleRhythmCue(scene = {}) {
  const mode = String(scene.audibleRhythmMode || "opening_then_rare").toLowerCase();
  return {
    enabled: scene.audibleRhythmEnabled === true,
    mode: ["opening_only", "opening_then_rare", "off"].includes(mode)
      ? mode
      : "opening_then_rare",
    bpm: clamp(scene.audibleRhythmBpm ?? scene.bpm, 24, 60, AUDIBLE_RHYTHM_DEFAULTS.bpm),
    level: clamp(scene.audibleRhythmLevel, 0.005, 0.08, AUDIBLE_RHYTHM_DEFAULTS.level),
    toneHz: clamp(scene.audibleRhythmToneHz, 220, 1400, AUDIBLE_RHYTHM_DEFAULTS.toneHz),
    accentToneHz: clamp(scene.audibleRhythmAccentToneHz, 220, 1600, AUDIBLE_RHYTHM_DEFAULTS.accentToneHz),
    tickDurationMs: clamp(scene.audibleRhythmTickDurationMs, 35, 180, AUDIBLE_RHYTHM_DEFAULTS.tickDurationMs),
    accentDurationMs: clamp(scene.audibleRhythmAccentDurationMs, 50, 240, AUDIBLE_RHYTHM_DEFAULTS.accentDurationMs),
    accentEvery: Math.round(clamp(scene.audibleRhythmAccentEvery, 2, 8, AUDIBLE_RHYTHM_DEFAULTS.accentEvery)),
    openingSeconds: clamp(scene.audibleRhythmOpeningSeconds, 15, 180, AUDIBLE_RHYTHM_DEFAULTS.openingSeconds),
    reanchorIntervalSeconds: clamp(
      scene.audibleRhythmReanchorIntervalSeconds,
      600,
      3600,
      AUDIBLE_RHYTHM_DEFAULTS.reanchorIntervalSeconds,
    ),
    reanchorSeconds: clamp(scene.audibleRhythmReanchorSeconds, 5, 45, AUDIBLE_RHYTHM_DEFAULTS.reanchorSeconds),
  };
}

export function rhythmTickIntervalMs(settings) {
  return 60_000 / clamp(settings?.bpm, 24, 60, AUDIBLE_RHYTHM_DEFAULTS.bpm);
}

export class AudibleRhythmCueController {
  constructor(audioContext, outputNode) {
    this.audioContext = audioContext;
    this.outputNode = outputNode;
    this.settings = normalizeAudibleRhythmCue();
    this.playing = false;
    this.phase = "idle";
    this.sessionKey = "";
    this.tickIndex = 0;
    this.tickTimer = null;
    this.phaseTimer = null;
    this.rareTimer = null;
  }

  apply(scene = {}) {
    this.settings = normalizeAudibleRhythmCue(scene);
    if (!this.settings.enabled || this.settings.mode === "off") {
      this.stop();
      return this.settings;
    }
    if (this.playing && this.phase === "idle") this.startSession(this.sessionKey || "default");
    return this.settings;
  }

  startSession(sessionKey = "default") {
    const normalizedKey = String(sessionKey || "default");
    if (normalizedKey !== this.sessionKey) {
      this.resetSession();
      this.sessionKey = normalizedKey;
    }
    this.phase = this.settings.enabled && this.settings.mode !== "off" ? "opening" : "idle";
    this.tickIndex = 0;
    this._clearTimers();
    if (this.playing && this.phase !== "idle") this._startPhaseTimer();
  }

  setPlaying(playing) {
    this.playing = Boolean(playing);
    this._clearTickTimer();
    if (!this.playing || !this.settings.enabled || this.settings.mode === "off") return;
    if (this.phase === "idle") this.startSession(this.sessionKey || "default");
    if (this.phase !== "idle") {
      this._startPhaseTimer();
      this._scheduleTick(0);
    }
  }

  resetSession() {
    this._clearTimers();
    this.phase = "idle";
    this.tickIndex = 0;
  }

  status() {
    const active = Boolean(
      this.playing
      && this.phase !== "idle"
      && this.settings.enabled
      && this.settings.mode !== "off"
    );
    return {
      active,
      enabled: this.settings.enabled === true,
      phase: this.phase,
      tickIndex: this.tickIndex,
      bpm: Number(this.settings.bpm || 0),
    };
  }

  stop() {
    this._clearTimers();
    this.phase = "idle";
    this.tickIndex = 0;
  }

  destroy() {
    this.stop();
    this.audioContext = null;
    this.outputNode = null;
  }

  _startPhaseTimer() {
    this._clearPhaseTimers();
    if (this.phase === "opening") {
      this.phaseTimer = window.setTimeout(() => {
        this.phase = "idle";
        this._clearTickTimer();
        if (this.settings.mode === "opening_then_rare") this._scheduleRarePhase();
      }, this.settings.openingSeconds * 1000);
    } else if (this.phase === "reanchor") {
      this.phaseTimer = window.setTimeout(() => {
        this.phase = "idle";
        this._clearTickTimer();
        if (this.settings.mode === "opening_then_rare") this._scheduleRarePhase();
      }, this.settings.reanchorSeconds * 1000);
    }
  }

  _scheduleRarePhase() {
    if (!this.playing || this.settings.mode !== "opening_then_rare") return;
    this.rareTimer = window.setTimeout(() => {
      if (!this.playing) return;
      this.phase = "reanchor";
      this._startPhaseTimer();
      this._scheduleTick(0);
    }, this.settings.reanchorIntervalSeconds * 1000);
  }

  _scheduleTick(delayMs) {
    this._clearTickTimer();
    if (!this.playing || this.phase === "idle") return;
    this.tickTimer = window.setTimeout(() => {
      this.tickTimer = null;
      if (!this.playing || this.phase === "idle") return;
      this._playTick();
      this._scheduleTick(rhythmTickIntervalMs(this.settings));
    }, Math.max(0, delayMs));
  }

  _playTick() {
    const context = this.audioContext;
    const output = this.outputNode;
    if (!context || !output || typeof context.createOscillator !== "function") return;
    try {
      const accent = this.tickIndex % this.settings.accentEvery === 0;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const now = context.currentTime + 0.005;
      const duration = (accent ? this.settings.accentDurationMs : this.settings.tickDurationMs) / 1000;
      const peak = this.settings.level * (accent ? 1.18 : 1);
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(accent ? this.settings.accentToneHz : this.settings.toneHz, now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), now + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
      oscillator.connect(gain);
      gain.connect(output);
      oscillator.start(now);
      oscillator.stop(now + duration + 0.02);
      this.tickIndex += 1;
    } catch {
      // A browser audio capability gap must never interrupt the main deck.
    }
  }

  _clearTickTimer() {
    if (this.tickTimer !== null) window.clearTimeout(this.tickTimer);
    this.tickTimer = null;
  }

  _clearPhaseTimers() {
    if (this.phaseTimer !== null) window.clearTimeout(this.phaseTimer);
    if (this.rareTimer !== null) window.clearTimeout(this.rareTimer);
    this.phaseTimer = null;
    this.rareTimer = null;
  }

  _clearTimers() {
    this._clearTickTimer();
    this._clearPhaseTimers();
  }
}
